import { useEffect } from 'react';

import {
    getBatchInstanceOpacities,
    PANEL_REPLACEMENT_ACTIVE_OPACITY,
    setBatchInstanceOpacities
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/batchedMeshUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

export const useReplacementOpacityHandler = (active: boolean, invalidate: () => void) => {
    useEffect(() => {
        if (!active) {
            return;
        }

        const initialState = usePlacementStore.getState();
        const initialBatch = initialState.batchedMeshes.physical;
        if (!initialBatch) {
            return;
        }

        const instanceIds = new Set<number>();
        for (const batchIds of initialState.instanceBatchIds.values()) {
            for (const instanceId of batchIds.physicalIds) {
                instanceIds.add(instanceId);
            }
        }

        const previousOpacities = getBatchInstanceOpacities(initialBatch, instanceIds);
        const dimmedOpacities = new Map(
            Array.from(instanceIds, (instanceId) => [instanceId, PANEL_REPLACEMENT_ACTIVE_OPACITY])
        );
        setBatchInstanceOpacities(initialBatch, dimmedOpacities);
        for (const { articleRef } of initialState.articleDragRefsMap.current.values()) {
            if (articleRef.current?.isMainCanvas) {
                initialState.updateInstanceMatrices(true, articleRef, false);
            }
        }
        invalidate();

        return () => {
            const state = usePlacementStore.getState();
            for (const { articleRef } of state.articleDragRefsMap.current.values()) {
                if (articleRef.current?.isMainCanvas) {
                    state.updateInstanceMatrices(true, articleRef, false);
                }
            }

            const currentBatch = state.batchedMeshes.physical;
            if (currentBatch === initialBatch) {
                const restoredOpacities = new Map<number, number>();
                for (const batchIds of state.instanceBatchIds.values()) {
                    for (const instanceId of batchIds.physicalIds) {
                        restoredOpacities.set(instanceId, previousOpacities.get(instanceId) ?? 1);
                    }
                }
                setBatchInstanceOpacities(currentBatch, restoredOpacities);
            } else if (currentBatch) {
                const defaultOpacities = new Map<number, number>();
                for (const batchIds of state.instanceBatchIds.values()) {
                    for (const instanceId of batchIds.physicalIds) {
                        defaultOpacities.set(instanceId, 1);
                    }
                }
                setBatchInstanceOpacities(currentBatch, defaultOpacities);
            }

            const selectedInstanceIds = new Set(Object.keys(state.articleInstanceSelections));
            for (const { articleRef } of state.articleDragRefsMap.current.values()) {
                const article = articleRef.current;
                if (!article) {
                    continue;
                }
                const articleSelectionIds = [...selectedInstanceIds].filter(
                    (instanceId) => instanceId in article.articleInstances
                );
                if (articleSelectionIds.length > 0) {
                    state.updateInstanceMatrices(true, articleRef, false, articleSelectionIds);
                }
            }
            invalidate();
        };
    }, [active, invalidate]);
};
